import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface RequestStepProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
}

export const RequestStep = ({
  title,
  subtitle,
  children,
  className,
}: RequestStepProps) => {
  return (
    <div className={cn("space-y-10 opacity-0 animate-fade-up", className)}>
      <div className="space-y-3 text-center max-w-xl mx-auto">
        <h1 className="text-headline">{title}</h1>
        {subtitle && <p className="text-caption">{subtitle}</p>}
      </div>
      
      <div className="max-w-lg mx-auto">
        {children}
      </div>
    </div>
  );
};

interface StepOptionProps {
  label: string;
  description?: string;
  isSelected?: boolean;
  onClick: () => void;
}

export const StepOption = ({
  label,
  description,
  isSelected,
  onClick,
}: StepOptionProps) => {
  return (
    <button
      onClick={onClick}
      role="radio"
      aria-checked={isSelected}
      className={cn(
        "w-full text-left p-4 md:p-5 border transition-all duration-300 min-h-[44px]",
        isSelected
          ? "border-accent bg-accent/5"
          : "border-border hover:border-muted-foreground"
      )}
    >
      <div className="flex justify-between items-start">
        <div>
          <p className="text-body font-medium">{label}</p>
          {description && (
            <p className="text-caption mt-1">{description}</p>
          )}
        </div>
        {isSelected && (
          <div className="w-2 h-2 bg-accent rounded-full mt-1.5" aria-hidden="true" />
        )}
      </div>
    </button>
  );
};

interface StepChipsProps {
  options: { id: string; label: string }[];
  selected: string[];
  onToggle: (id: string) => void;
  maxSelection?: number;
}

export const StepChips = ({
  options,
  selected,
  onToggle,
  maxSelection,
}: StepChipsProps) => {
  return (
    <div className="flex flex-wrap gap-3">
      {options.map((option) => {
        const isSelected = selected.includes(option.id);
        const isDisabled = maxSelection && !isSelected && selected.length >= maxSelection;
        
        return (
          <button
            key={option.id}
            onClick={() => !isDisabled && onToggle(option.id)}
            disabled={isDisabled}
            className={cn(
              "px-4 py-2.5 border text-sm transition-all duration-200 min-h-[44px]",
              isSelected
                ? "border-accent bg-accent/5 text-foreground"
                : "border-border text-muted-foreground hover:border-muted-foreground hover:text-foreground",
              isDisabled && "opacity-40 cursor-not-allowed"
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

interface StepProgressProps {
  steps: string[];
  currentIndex: number;
}

export const StepProgress = ({ steps, currentIndex }: StepProgressProps) => {
  return (
    <div className="flex items-center gap-4">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center gap-3">
          <span
            className={cn(
              "text-micro transition-colors duration-500",
              i < currentIndex ? "text-accent" : i === currentIndex ? "text-foreground font-medium" : "text-muted-foreground/40"
            )}
          >
            {label}
          </span>
          {i < steps.length - 1 && (
            <span className="text-border">·</span>
          )}
        </div>
      ))}
    </div>
  );
};
